import {
  Box,
  Checkbox,
  CircularProgress,
  Fade,
  FormControlLabel,
  Link,
  Stack,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { LicenseInfo } from '@mui/x-license';
import {
  Analytics,
  BreakpointIndicator,
  ConfirmationDialog,
  DemoDataEvent,
  Environment,
  Theme,
} from 'klayowebshared';
import React, { Component, Suspense } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import type { Location } from 'react-router-dom';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Userpilot } from 'userpilot';
import { AppContext } from './common/AppContext';
import axiosClient from './common/AxiosClient';
import { getAuthClient } from './common/auth/authClient';
import { EventBus } from './common/EventBus';
import { Utils } from './common/Utils';
import { ErrorModel } from './common/models/ErrorModel';
import { LoadingModel } from './common/models/LoadingModel';
import { isPostHogLive, resetAnalyticsIdentity, track, trackPageView } from './common/track';
import { AppBar } from './components/AppBar';
import { AppDrawer } from './components/AppDrawer';
import { KitDrawer } from './components/KitDrawer';
import { KitFloatingButton } from './components/KitFloatingButton';
import { LoadingBar } from './components/LoadingBar';
import { NotificationDrawer } from './components/NotificationDrawer';
import { NotificationPopup } from './components/NotificationPopup';
import { Snackbar } from './components/Snackbar';
import { Dashboard_mode, PLATFORM_ENTERED_KEY, TEAM_LIST_VIEW_MODE } from './constants';
import { getMyGroupsNavGroups } from './data/groups/Group';
import { Organization } from './data/Organization';
import { Notification } from './data/notifications/Notification';
import { NotificationsData } from './data/notifications/NotificationsData';
import { CurrentUser } from './data/users/CurrentUser';
import { ImpersonateUser } from './data/users/ImpersonateUser';
import { RootState } from './interfaces/common';
import { withAppData, WithAppDataProps } from './providers/AppDataProvider';
import { TeamProvider } from './providers/TeamProvider';
import { AppPages } from './router';
import { PageComponents } from './router/components';
import { withNavigate, WithRouterProps } from './router/withNavigate';
import {
  onCloseChecklistPreviewDialog,
  onOpenChecklistPreviewDialog,
  onSaveChecklistAnswers,
} from './store/checklist.slice';
import {
  AppContextType,
  AppOwnProps,
  AppPage,
  AppState,
  DashboardMode,
  GroupItem,
  SettingsChangeEvent,
  SettingsSubPage,
} from './types/app.types';
import { DataContext } from './types/data.types';
import { ContactUsDialog } from './views/other/ContactUsDialog';
import { DevDialog } from './views/other/DevDialog';
import { ErrorBoundary } from './views/other/ErrorBoundary';
import { ErrorPage } from './views/other/ErrorPage';
import { InactiveView } from './views/other/InactiveView';
import { SwitchUserDialog } from './views/other/SwitchUserDialog';
import { UnavailableView } from './views/other/UnavailableView';
import CheckListsPreviewDialog from './views/settings/CheckListsPreviewDialog';
import { Settings } from './views/settings/Settings';
import { DEFAULT_DATASET_CODE } from './views/try/datasets';
import { MY_TEAM_DATASET_CODE } from './views/try/datasets/myTeam';
import { getPreviewUrlState, isPreviewArmed } from './views/try/mock/previewMode';
import { demoParamCorrection } from './views/try/preview/demoParam';
import {
  clearPendingDemoStamp,
  enterPreview,
  exitPreview,
  isMyTeamActive,
  isMyTeamAvailable,
  peekPendingDemoStamp,
  PREVIEW_NAV_SLUGS,
  previewUserpilotId,
  switchPreviewDataset,
} from './views/try/preview/previewIdentity';

// Re-export types that other files may depend on
export type {
  AppContextType,
  AppPage,
  DashboardMode,
  GroupItem,
  SettingsChangeEvent,
  SettingsSubPage,
};

const mapState = (state: RootState) => ({
  store: state,
});

const mapDispatch = {
  onOpenChecklistPreviewDialog,
  onCloseChecklistPreviewDialog,
  onSaveChecklistAnswers,
};

const withRedux = connect(mapState, mapDispatch);

// Height (px) of the Try It Now "sample data" bar; a matching in-flow spacer reserves this space.
const PREVIEW_BANNER_HEIGHT = 48;
// Persists the "Don't show this dialog again" choice for the Exit demo confirmation (per browser).
const EXIT_DEMO_SKIP_KEY = 'klayo.preview.skipExitConfirm';

type ReduxProps = ConnectedProps<typeof withRedux>;
type Props = AppOwnProps & ReduxProps & WithRouterProps & WithAppDataProps;

export class App extends Component<Props, AppState> {
  appContext: AppContextType;
  loadingModel: LoadingModel;
  loadingBar: LoadingBar | null;
  appBar: AppBar | null;
  appDrawer: AppDrawer | null;
  kitDrawer: React.ElementRef<typeof KitDrawer> | null;
  notificationDrawer: React.ElementRef<typeof NotificationDrawer> | null;
  notificationPopup: React.ElementRef<typeof NotificationPopup> | null;
  dataVersion: number;
  blockNavigation: boolean;
  navigateAwayCallback: ((e: React.MouseEvent) => void) | null;
  allowNavigationCallback: ((e: React.MouseEvent) => void) | null;
  notifications: Record<string, NotificationsData>;
  notificationsStarted: boolean;
  currentBreakpoint: string;
  pendingNavigationEvent: React.MouseEvent | null;
  // employeeId last sent to Userpilot.identify, so we identify once per user rather than on every
  // user/org refetch.
  userpilotIdentifiedId: string | null = null;

  setLoadingBarRef: (loadingBar: LoadingBar | null) => void;
  setAppBarRef: (appBar: AppBar | null) => void;
  setAppDrawerRef: (appDrawer: AppDrawer | null) => void;
  setKitDrawerRef: (kitDrawer: React.ElementRef<typeof KitDrawer> | null) => void;
  setNotificationDrawerRef: (
    notificationDrawer: React.ElementRef<typeof NotificationDrawer> | null
  ) => void;
  setNotificationPopupRef: (
    notificationPopup: React.ElementRef<typeof NotificationPopup> | null
  ) => void;

  static defaultProps: Partial<AppOwnProps> = {
    settingLandingEnabled: false,
  };

  static defaultPage = AppPages.profile as AppPage;
  static allowDevBar = true;
  urlParams: URLSearchParams;

  constructor(props: Props) {
    super(props);

    const themeName = 'v2'; //window.localStorage.getItem('theme')
    this.urlParams = new URLSearchParams(window.location.search);

    this.loadingBar = null;
    this.appBar = null;
    this.appDrawer = null;
    this.kitDrawer = null;
    this.notificationDrawer = null;
    this.notificationPopup = null;
    this.pendingNavigationEvent = null;

    this.state = {
      loading: 0,
      error: null,
      admin: false,
      organization: null,
      user: null,
      theme: this.getTheme(themeName),
      themeName: themeName,
      activePage: App.defaultPage,
      debugParams: this.urlParams.get('debug'),
      showDevDialog: false,
      setLoading: this.setLoading.bind(this),
      showSwitchUserDialog: false,
      unreadNotifications: null,
      acceptTermsError: null,
      showInactiveView: false,
      demoOrgResetting: false,
      showExitDemoConfirm: false,
      dontShowExitDemoAgain: false,
      showContactUsView: false,
      showBillingPlans: false,
      drawerOpen: false,
      showDeviceBreakpoints: Utils.getDevOption('devOpts_ShowDeviceBreakpoints'),
      treeMode: Utils.checkLocalStorageOption('treeMode') || false,
      dataVersion: 0,
    };

    this.appContext = this.createAppContext();

    this.loadingModel = new LoadingModel();
    this.loadingModel.debug = this.state.debugParams != null;

    this.setLoadingBarRef = (loadingBar: LoadingBar | null) => {
      this.loadingBar = loadingBar;
    };

    this.setAppBarRef = (appBar: AppBar | null) => {
      this.appBar = appBar;
    };

    this.setAppDrawerRef = (appDrawer: AppDrawer | null) => {
      this.appDrawer = appDrawer;
    };

    this.setKitDrawerRef = (kitDrawer: React.ElementRef<typeof KitDrawer> | null) => {
      this.kitDrawer = kitDrawer;
    };

    this.setNotificationDrawerRef = (
      notificationDrawer: React.ElementRef<typeof NotificationDrawer> | null
    ) => {
      this.notificationDrawer = notificationDrawer;
    };

    this.setNotificationPopupRef = (
      notificationPopup: React.ElementRef<typeof NotificationPopup> | null
    ) => {
      this.notificationPopup = notificationPopup;
    };

    this.dataVersion = 0;

    this.blockNavigation = false;
    this.navigateAwayCallback = null;
    this.allowNavigationCallback = null;

    this.notifications = {};
    this.notificationsStarted = false;

    if (Environment.isLocal) this.changeFavIcon('/favicon-localhost.ico');
    else if (Environment.isTest) this.changeFavIcon('/favicon-test.ico');
    else if (Environment.isDev) this.changeFavIcon('/favicon-dev.ico');

    LicenseInfo.setLicenseKey(
      '16bedf6ca5b0f82ecc381d58ca1c1854Tz0xMjQ0MDcsRT0xODAwMjMwMzk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI='
    );

    this.currentBreakpoint = this.state.theme?.getCurrentBreakpoint()?.[0] ?? 'md';
    window.addEventListener('resize', this.onWindowResize.bind(this));

    (window as unknown as { signOut: () => void }).signOut = this.signOut.bind(this);
    (window as unknown as { printTestInfo: () => string }).printTestInfo =
      this.printTestInfo.bind(this);

    window.addEventListener('popstate', this.onPopState.bind(this));
  }

  onWindowResize(_e: UIEvent): void {
    const { theme } = this.state;
    const breakpoint = theme?.getCurrentBreakpoint()?.[0] ?? 'md';

    if (breakpoint !== this.currentBreakpoint) {
      this.currentBreakpoint = breakpoint;
      this.forceUpdate();
    }
  }

  onPopState(_e: PopStateEvent): void {
    this.blockNavigation = false;
  }

  createAppContext(): AppContextType {
    return {
      isAppContext: true,
      msal: this.props.msal,
      setLoading: this.setLoading.bind(this),
      showNotifications: this.showNotifications.bind(this),
      devMode: false,
      newDesign: this.state.themeName === 'v2',
      eventBus: new EventBus(),
    };
  }

  static getTestDetails(organization: Organization | null, user: CurrentUser | null): string {
    const info: string[] = [];
    info.push('Version:\t\t\t' + import.meta.env.VITE_APP_VERSION);
    info.push('Environment:\t\t' + Environment.get() + ' (' + window.location.host + ')');
    info.push('URL:\t\t\t\t' + window.location.href);
    if (user) {
      info.push(
        'Impersonated user:\t' +
          (user.impersonating
            ? user.fullName +
              ' (Job: ' +
              user.jobTitle +
              (user.isDeveloper ? ', Dev' : '') +
              (user.isOrganizationAdmin ? ', Org Admin' : '') +
              (user.isBillingUser ? ', Billing Admin' : '') +
              (user.isTeamLead ? ', Team Lead' : '') +
              (user.hasGroup ? ', Group manager' : '') +
              ')'
            : '<None>')
      );
      info.push(
        'Logged in user:\t\t' +
          user.loggedInUserFullName +
          ' (' +
          (user.loggedInUserJobTitle ? 'Job: ' + user.loggedInUserJobTitle + ', ' : '') +
          'Email: ' +
          user.email +
          ')'
      );
    }
    if (organization)
      info.push(
        'Organization:\t\t' + organization.name + (organization.isDemo ? ' (Demo org)' : '')
      );

    return info.join('\n');
  }

  printTestInfo(): string {
    return '';
  }

  getTheme(_name: string): typeof Theme {
    return new Theme(
      createTheme({
        name: 'v2',
        palette: {
          primary: {
            light: '#0000FF',
            main: Theme.getStyleVar('--neon-blue-nb-400'),
            dark: '#000000',
          },
          secondary: {
            light: '#ff0000',
            main: Theme.getStyleVar('--neon-blue-nb-400'),
            dark: '#ff0000',
          },
          error: {
            main: Theme.getStyleVar('--torch-red-tr-500'),
          },
        },
        breakpoints: {
          values: {
            xs: 0,
            sm: 600,
            md: 900,
            lg: 1200,
            xl: 1920,
          },
        },
        typography: {
          fontFamily: 'Inter',
        },
      } as Parameters<typeof createTheme>[0])
    );
  }

  componentDidMount(): void {
    this.showPage(App.defaultPage);

    // Covers a refresh (or a hand-typed URL) landing inside an armed demo without the param, and
    // strips a forwarded one outside a demo — componentDidUpdate only sees navigations.
    this.correctDemoParam(false);

    // Preview sandbox: don't boot analytics (Hotjar/GTM) — it would receive the prospect's real PII.
    if (!isPreviewArmed()) {
      Analytics.initialize();
    }

    // User and organization are now fetched via AppDataProvider
    // Check if data is already available from provider
    this.syncAppDataToState();
  }

  componentDidUpdate(prevProps: Props): void {
    const { appData } = this.props;
    const prevAppData = prevProps.appData;

    // Userpilot is a SPA: it doesn't observe route changes, so signal it on every navigation. The
    // explicit destination URL is what fixes the old bug (pages like /team not registering) — the
    // argument-less reload() didn't reliably update Userpilot's current page. DOM-targeted tours are
    // caught by Userpilot's own mutation observer as lazy pages mount. Fires in the preview sandbox
    // too (synthetic route URL, no PII) so demo-page tours re-scan on navigation.
    if (this.props.location !== prevProps.location) {
      // When the demo param needs restoring, skip THIS page view and let the corrected URL's own
      // update report it. Must not `return` — the appData diffs below are edge-triggered, so
      // skipping them here loses them for good rather than deferring them.
      if (!this.correctDemoParam(true)) {
        const { pathname, search } = this.props.location;
        trackPageView(pathname + search);
      }
    }

    // Sync user from AppDataProvider to state and context
    if (appData.user !== prevAppData.user && appData.user) {
      this.onUserLoaded(appData.user);
    }

    // Sync organization from AppDataProvider to state and context
    if (appData.organization !== prevAppData.organization && appData.organization) {
      this.onOrganizationLoaded(appData.organization);
    }

    // Update AppDrawer nav when groups change
    if (appData.groups !== prevAppData.groups && appData.groups && this.appDrawer) {
      this.appDrawer.updateNav();
    }

    // Update loading state
    if (appData.userLoading !== prevAppData.userLoading) {
      this.setLoading('user', appData.userLoading);
    }
    if (appData.organizationLoading !== prevAppData.organizationLoading) {
      this.setLoading('org', appData.organizationLoading);
    }

    // Handle errors
    if (appData.userError && appData.userError !== prevAppData.userError) {
      this.showError(new ErrorModel(ErrorModel.Severity.FAIL, appData.userError, 'User API'));
    }
    if (appData.organizationError && appData.organizationError !== prevAppData.organizationError) {
      this.showError(
        new ErrorModel(ErrorModel.Severity.FAIL, appData.organizationError, 'Organization API')
      );
    }
  }

  /**
   * Sync initial app data from provider to state (called on mount)
   */
  syncAppDataToState(): void {
    const { appData } = this.props;
    if (appData.user) {
      this.onUserLoaded(appData.user);
    }
    if (appData.organization) {
      this.onOrganizationLoaded(appData.organization);
    }
  }

  /**
   * Handle user data loaded from AppDataProvider
   */
  onUserLoaded(user: CurrentUser): void {
    this.appContext.devMode = user && user.isDeveloper;
    this.appContext.user = user;
    this.appContext.canChangeUser = user && user.canChangeUser;
    this.appContext.canChangeOrg = user && user.canChangeOrg;

    this.setState({
      user,
      showInactiveView: user.status === CurrentUser.status.inactive,
    });

    // Userpilot identify is deferred to identifyUserpilot() so it fires once BOTH user and org are
    // available (org loads after user) — org-derived attributes like hasMyKlayo must be accurate.
    this.identifyUserpilot();

    if (
      (user.isOrganizationAdmin === true || user.isBillingUser === true) &&
      user.hasAcceptedTerms === false
    ) {
      this.props.history.push('/' + AppPages.signup.slug);
    }
  }

  /**
   * Keep the `demo=<solutionCode>` param on the URL for the whole of an armed Try It Now session.
   *
   * Userpilot targets the demo tours by page URL, but pages rebuild their own URLs constantly (Team
   * reserializes its query from the filter model; the tab strip and app nav build bare NavLink
   * targets), so any of them can drop the param and end the targeting for the rest of the session.
   * Re-deriving it here from arming state covers every navigation at one point, and means a stray
   * `?demo=` on a real customer's URL is never honoured — only an armed session gets one.
   *
   * Returns true when it corrected, so the caller can let the corrected URL's own update report the
   * page view rather than the momentary one. No loop: the replaced URL satisfies the check.
   *
   * @param routerReady false on mount. react-router's `navigate()` is inert until the layout effect
   * of the provider ABOVE us has run, and React commits layout effects child-first — so a replace
   * from componentDidMount silently does nothing. (StrictMode double-invokes mount, so it appears to
   * work in dev and fails in production.) Only the query changes, so correct the address bar
   * directly instead, and report the page view here since no location change will follow.
   */
  correctDemoParam(routerReady: boolean): boolean {
    const { pathname, search, hash } = this.props.location;
    const corrected = demoParamCorrection(pathname, search, getPreviewUrlState(), hash);
    if (!corrected) return false;

    if (routerReady) {
      this.props.history.replace(corrected);
    } else {
      window.history.replaceState(null, '', corrected);
      trackPageView(corrected);
    }
    return true;
  }

  /**
   * Identify the current user to Userpilot once per login so flows can target segments. Runs only
   * once BOTH user and org are loaded (org arrives after user) so org-derived attributes such as
   * hasMyKlayo are accurate. Live sessions send the opaque employeeId only (no name/email); the
   * "Try It Now" preview uses a per-session synthetic id so the prospect's real employeeId is never
   * sent. Skipped while impersonating so an admin's Userpilot session isn't re-identified.
   *
   * Also flushes the pending demo stamp — persistent cross-session state, so this is not a pure
   * identify: a successful send consumes it.
   */
  identifyUserpilot(): void {
    const user = this.appContext.user;
    const organization = this.appContext.organization;
    if (!user || !organization || user.impersonating) return;

    const inPreview = isPreviewArmed();
    const userpilotId = inPreview ? previewUserpilotId() : user.employeeId;
    if (this.userpilotIdentifiedId === userpilotId) return;
    this.userpilotIdentifiedId = userpilotId;

    // Flush any queued "tried a demo" stamp onto the REAL profile. Only outside the sandbox: in
    // preview the id is a throwaway per-session one, so stamping there would record nothing useful.
    // Keyed by employeeId so a stamp can only ever land on the profile that earned it. Sending it
    // from a settled page load (rather than enterPreview's fire-and-navigate) is what makes it
    // actually arrive.
    const demos = inPreview ? null : peekPendingDemoStamp(user.employeeId);

    // Telemetry must never break the caller (onUserLoaded goes on to redirect for terms). The stamp
    // survives an identify that throws, so a failure retries next load instead of vanishing — but it
    // is cleared BEFORE the events, so a throw mid-loop can't re-send the properties on the next
    // load. (identify is fire-and-forget, so this guards synchronous failures only.)
    try {
      Userpilot.identify(userpilotId, {
        isTrainingManager: !!user.isTrainingManager,
        // My Klayo is enabled unless the org's feature flag is explicitly false.
        hasMyKlayo: organization.enabledFeatures?.MyKlayo !== false,
        // demo_intent stays the most recent solution (what segments key off); demos_tried lists all
        // of them, so trying several before returning doesn't hide the earlier ones.
        ...(demos ? { demo_intent: demos[demos.length - 1], demos_tried: demos.join(',') } : {}),
      });
      // Both legs are held back together unless PostHog can actually capture here. A failed "Show
      // my team" bootstrap lands the user in the normal app with `isPreviewArmed()` false but
      // PostHog never initialised for this document (see isPostHogLive) — flushing there would
      // clear the queue and lose every demo in it, earlier successful ones included, with no retry.
      // Holding BOTH keeps them in step, so the next load sends each exactly once rather than
      // re-sending Userpilot's.
      if (demos && isPostHogLive()) {
        clearPendingDemoStamp();
        // One event per solution tried, each naming its solution — so the funnel counts demos, not
        // just people, and can be broken down without joining back to the profile property.
        //
        // Two passes, PostHog first: track() cannot throw (it guards internally), so a Userpilot
        // SDK failure part-way through can't cost us the remaining PostHog records — the queue is
        // cleared by this point, so anything skipped is gone for good. (A throw from identify()
        // above skips both passes, but that path leaves the queue intact for the next load.)
        // This is the only way a COMPLETED demo reaches PostHog: the sandbox never captures, so
        // `demo_data:entered` at launch plus this on return are the whole picture.
        demos.forEach((solution) => track(DemoDataEvent.Viewed, { solutionCode: solution }));
        // Deliberately not routed through track()'s USERPILOT_EVENTS map: that map forwards the
        // event name only, so folding this into it would silently drop the { solution } property.
        demos.forEach((solution) => Userpilot.track('viewed_demo_data', { solution }));
      }
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * Handle organization data loaded from AppDataProvider
   */
  onOrganizationLoaded(org: Organization): void {
    // Preview sandbox: never send the real user/org to Hotjar/GTM.
    if (!isPreviewArmed()) {
      Analytics.identify(this.state.user, org);
    }

    this.appContext.organization = org;

    // Org is available now — (re)attempt the Userpilot identify so org-derived attributes
    // (hasMyKlayo) are included. Fires at most once thanks to the userpilotIdentifiedId guard.
    this.identifyUserpilot();

    this.setState({
      showInactiveView:
        this.state.showInactiveView || (org.status === Organization.status.inactive && !org.isDemo),
      demoOrgResetting: org.demoOrgStatus === Organization.status.resetting,
    });

    // Start notifications connection when organization is loaded.
    // Preview sandbox: skip — the SignalR socket bypasses the axios mock and would open a real
    // authenticated connection to the live backend.
    if (!this.notificationsStarted && !isPreviewArmed()) {
      this.notificationsStarted = true;
      (
        NotificationsData.startNotifications(
          this.appContext as DataContext
        ) as Promise<NotificationsData>
      )
        .then((n) => this.onInitNotifications(n))
        .catch((e: unknown) => {
          console.warn('Notifications connection failed; live updates disabled', e);
          this.showError(new ErrorModel(ErrorModel.Severity.INFO, e, 'Notifications connection'));
        });
    }
  }

  /**
   * Refresh user data - triggers React Query refetch
   * Called after user switch or when data needs to be refreshed
   */
  async getLoginUser(_updateCache?: boolean): Promise<void> {
    // Reset notifications state for new user context
    this.notificationsStarted = false;
    // Trigger React Query refetch - componentDidUpdate will handle the response
    this.props.appData.refetchUser();
    this.props.appData.refetchOrganization();
    this.props.appData.refetchGroups();
  }

  onInitNotifications(notifications: NotificationsData): void {
    this.notifications[notifications.mode.name] = notifications;

    notifications.unread.onChange = this.onReadNotificationsChange.bind(this);
    notifications.onNotificationReceived = this.onNotificationReceived.bind(this);

    if (this.appBar) this.appBar.setUnreadNotifications(notifications.unread.count);
    if (this.appDrawer) this.appDrawer.setUnreadNotifications(notifications.unread.count);
    if (this.notificationDrawer)
      this.notificationDrawer.setState({ notifications: this.notifications });
  }

  /**
   * @deprecated Organization is now fetched via AppDataProvider
   * This method is kept for backwards compatibility but just triggers a refetch
   */
  getOrganisation(): void {
    this.props.appData.refetchOrganization();
  }

  // Groups are now fetched via AppDataProvider and passed as props.appData.groups

  loadNotifications(mode: DashboardMode, groupId?: string | null): void {
    //if (this.notifications[mode.name] !== undefined) return;
    if (isPreviewArmed()) return; // no real notification fetches in the preview sandbox
    this.setLoading('notifications', true);

    NotificationsData.getNotificationsData(this.appContext, mode)
      .then((n) => this.onInitNotifications(n))
      .catch((e: unknown) =>
        this.showError(new ErrorModel(ErrorModel.Severity.INFO, e, 'Notification API'))
      )
      .finally(() => this.setLoading('notifications', false));
  }

  setTheme(theme: string): void {
    this.setLoading('switchTheme', true);
    this.appContext.newDesign = theme === 'v2';

    window.localStorage.setItem('theme', theme);
    this.setState({ themeName: theme, theme: this.getTheme(theme) }, () => {
      this.setLoading('switchTheme', false);
    });
  }

  onReadNotificationsChange(count: number): void {
    if (this.appBar) this.appBar.setUnreadNotifications(count);
  }

  onLoadNotifications(mode: DashboardMode, groupId?: string | null): void {
    this.loadNotifications(mode, groupId);
  }

  onNotificationReceived(notif: Notification | null, numUnread: number): void {
    if (notif && notif.type === Notification.type.employeeAddedAsGroupManager) {
      //Todo: refresh group menu
      return;
    }

    if (this.notificationDrawer) this.notificationDrawer.onNotificationReceived(notif, numUnread);
    if (this.appBar) this.appBar.onNotificationReceived(notif, numUnread);
    if (this.notificationPopup) this.notificationPopup.onNotificationReceived(notif, numUnread);

    if (notif && notif.type == Notification.type.demoOrgResetting) {
      this.setState({ demoOrgResetting: true });
      return;
    } else if (notif && notif.type == Notification.type.demoOrgResetDone) {
      setTimeout(() => window.location.reload(), 1000);
      return;
    }

    Object.values(AppPages).map((page: AppPage) => {
      if (page.ref.current)
        (
          page.ref.current as {
            onNotificationReceived?: (notif: Notification | null, numUnread: number) => void;
          }
        ).onNotificationReceived?.(notif, numUnread);
    });
  }

  showError(e: ErrorModel): void {
    if (e.isFail()) {
      this.loadingModel.resetAll();
      this.setLoading(null, false);
    }
    this.setState({ error: e });
  }

  onShowError(e: ErrorModel): void {
    this.showError(e);
  }

  isDebugMode(mode: string): boolean {
    return !!(this.state.debugParams && this.state.debugParams.includes(mode));
  }

  setLoading(event: string | null, loading: boolean): void {
    this.loadingModel.setLoading(event, loading);
    if (this.loadingBar) this.loadingBar.setLoading(this.loadingModel.isLoading);
    this.appContext.eventBus.dispatch(
      this.loadingModel.isLoading ? EventBus.event.loadingStart : EventBus.event.loadingEnd,
      null
    );
    this.appContext.eventBus.dispatch(event, loading);
  }

  onSignOut(): void {
    this.setLoading('signOut', true);

    axiosClient.post('/User/ClearCookies').finally(() => {
      this.signOut();
    });
  }

  signOut(): void {
    localStorage.removeItem(TEAM_LIST_VIEW_MODE);
    Utils.clearLoginSessionUiFlags();
    // Clear the analytics identity so the next login on a shared browser starts a fresh session,
    // including the per-tab guard — otherwise the next person never fires platform:entered.
    resetAnalyticsIdentity();
    sessionStorage.removeItem(PLATFORM_ENTERED_KEY);
    this.userpilotIdentifiedId = null;
    // Reset URL to root before logout so the next login does not restore the current page URL
    window.history.replaceState(null, '', '/');
    getAuthClient()
      .logout()
      .then(() => {})
      .catch((e: unknown) => {
        console.error(e);
      })
      .finally(() => this.setLoading('signOut', true));
  }

  onSignIn(): void {}

  onShowSwitchUserDialog(): void {
    this.setState({ showSwitchUserDialog: true });
  }

  onSwitchUser(_e: React.MouseEvent | null, user: CurrentUser | null): void {
    this.setLoading('switchUser', true);
    ImpersonateUser.post(this.appContext, user ? user.employeeId : null)
      .then(() => {
        // Stop the outgoing identity carrying into the impersonated session. The employee's own
        // profile is protected by the `impersonating` guards in useAnalyticsIdentity and
        // identifyUserpilot, which stop us re-identifying as them. platform:entered is deliberately
        // NOT cleared here — this is the same person, and clearing it double-counts their entry.
        resetAnalyticsIdentity();
        window.location.href = '/';
        localStorage.removeItem(TEAM_LIST_VIEW_MODE);
      })
      .catch((e: unknown) => {
        this.showError(new ErrorModel(ErrorModel.Severity.INFO, e, 'Switch user API'));
      })
      .finally(() => this.setLoading('switchUser', false));
  }

  closeSwitchUserDialog(): void {
    this.setState({ showSwitchUserDialog: false });
  }

  showPage(page: AppPage): void {
    this.setState({ activePage: page });
  }

  onCloseErrorSnackbar(_e: React.SyntheticEvent | Event): void {
    if (this.state.error && !this.state.error.isFail()) this.setState({ error: null });
  }

  onCloseNotificationSnackbar(_e: React.SyntheticEvent | Event): void {
    if (this.state.error && !this.state.error.isFail()) this.setState({ error: null });
  }

  onShowNotifications(_e: React.MouseEvent): void {
    this.showNotifications(Dashboard_mode.personal, true);
  }

  showNotifications(mode: DashboardMode, show: boolean): void {
    if (!this.notificationDrawer) return;
    this.notificationDrawer.open(mode, show);
  }

  onNotificationClick(_e: React.MouseEvent, notif: Notification, groupId?: string | null): void {
    const { history } = this.props;

    if (notif.isMode(Dashboard_mode.personal)) {
      if (notif.type === Notification.type.participantAcknowledged) {
        history.push('/' + AppPages.practicals.slug + '/edit/' + notif.practicalId);
      } else if (notif.type === Notification.type.participateAcknowedgementRequest) {
        history.push(
          '/' + AppPages.practicals.slug + '/confirmation/' + notif.practicalParticipantId
        );
      } else if (notif.type === Notification.type.courseEnrolmentRequested) {
        // Approver: open the request, as the "enrolment requested" email does.
        history.push(
          '/' + AppPages.feedback.slug + '/learning?request_id=' + notif.enrolmentRequestId
        );
      } else if (Notification.isEnrolmentRequestType(notif.type)) {
        // Learner: the request list is approver-only, so open the course instead.
        history.push('/my-klayo/courses/' + notif.courseId);
      } else {
        history.push('/' + AppPages.profile.slug + '/competencies#' + notif.employeeAttributeId);
      }
    } else if (notif.isMode(Dashboard_mode.team)) {
      history.push('/' + AppPages.feedback.slug + '/review/' + notif.employeeAttributeId);
    } else if (notif.isMode(Dashboard_mode.groups)) {
      history.push(
        '/' +
          AppPages.feedback.slug +
          '/' +
          (groupId ? groupId : notif.groupId) +
          '/review/' +
          notif.employeeAttributeId
      );
    }

    //This call will trigger SignalR to push an update with the new unread count

    axiosClient
      .put('/Notifications', {
        notificationId: notif.id,
        notificationState: Notification.state.read.index,
      })
      .then((response: { data: { totalNumberOfUnreadNotification: number } }) => {
        if (this.appBar)
          this.appBar.setUnreadNotifications(response.data.totalNumberOfUnreadNotification);
      });
  }

  onSettingsChange(_change: SettingsChangeEvent): void {
    this.appContext.eventBus.dispatch(EventBus.event.dataChanged, null);

    this.setState({ dataVersion: this.state.dataVersion + 1 });
  }

  getPageProps(page: AppPage): Record<string, unknown> {
    if (page === AppPages.dashboard)
      return { onNotificationClick: this.onNotificationClick.bind(this) };
    else if (page === AppPages.settings)
      return {
        onChangeCurrentUserFromSettings: this.onChangeCurrentUserFromSettings.bind(this),
        onDemoSwitch: this.onDemoSwitch.bind(this),
        onContactUs: this.onShowContactForm.bind(this),
        onChange: this.onSettingsChange.bind(this),
      };
    else if (page === AppPages.signup)
      return {
        onComplete: this.onSignUpComplete.bind(this),
      };
    return {};
  }

  onChangeCurrentUserFromSettings(_employee: { employeeId: string }): void {
    this.getLoginUser(true);
  }

  onOpenDashboard(): void {
    this.props.history.push('/' + AppPages.dashboard.slug);
  }

  onOpenDevDialog(): void {
    const user = this.state.user;
    if ((user && user.isDeveloper) || Environment.isDev || Environment.isLocal)
      this.setState({ showDevDialog: true });
  }

  onOpenSettings(): void {
    const { history } = this.props;
    history.push('/' + AppPages.settings.slug);
  }

  onMyAccount(): void {
    const { history } = this.props;
    history.push('/' + AppPages['my-account'].slug);
  }

  onCloseDevDialog(_e: React.SyntheticEvent<Element, Event> | undefined): void {
    this.setState({ showDevDialog: false });
  }

  onCloseContactUsDialog(_e?: React.MouseEvent | React.KeyboardEvent): void {
    const { history } = this.props;
    const { contactUsRedirect } = this.state;

    this.setState({ showContactUsView: false }, () => history.push(contactUsRedirect || '/'));
  }

  changeFavIcon(path: string): void {
    document.querySelectorAll("link[rel*='icon']").forEach((l: Element) => {
      (l as HTMLLinkElement).href = path;
    });
  }

  onLogoClick(_e: React.MouseEvent): void {
    this.props.history.push('/' + AppPages.profile.slug);
  }

  onAcceptTerms(): void {
    const { user } = this.state;

    this.setLoading('acceptTerms', true);

    axiosClient
      .put('/Organization/AcceptTerms')
      .then(() => {
        (Analytics as unknown as { event: (name: string) => void }).event('acceptTerms');
        if (user) {
          user.hasAcceptedTerms = true;
          this.setState({ user });
        }
      })
      .finally(() => {
        this.setLoading('acceptTerms', false);
      });
  }

  onDemoSwitch(_e: React.MouseEvent | null, demo: boolean): void {
    this.setLoading('switchDemo', true);

    axiosClient
      .post('/Organization/SwitchDemo?switchToDemo=' + demo)
      .then(() => {
        //window.location.reload();
        window.location.href = demo ? '/?ref=ds' : '/';
      })
      .finally(() => {
        this.setLoading('switchDemo', false);
      });
  }

  // Exit demo: show the confirmation dialog unless the user previously chose to skip it, in which
  // case exit straight away.
  onExitDemoClick(): void {
    if (localStorage.getItem(EXIT_DEMO_SKIP_KEY) === '1') {
      exitPreview();
      return;
    }
    this.setState({ showExitDemoConfirm: true, dontShowExitDemoAgain: false });
  }

  onExitDemoConfirm(): void {
    if (this.state.dontShowExitDemoAgain) localStorage.setItem(EXIT_DEMO_SKIP_KEY, '1');
    exitPreview();
  }

  onUpgradePlan(): void {
    this.setState({ error: null, showInactiveView: false });
    this.props.history.push(Settings.subPages.billing.paths[0] + '/plan');
  }

  onShowContactForm(_e: React.MouseEvent | null, contactUsRedirect: string): void {
    this.setState({ showContactUsView: true, contactUsRedirect });
  }

  onMenuButtonClick(_e: React.MouseEvent): void {
    this.setState({ drawerOpen: !this.state.drawerOpen });
  }

  onDrawerClose(_e: React.MouseEvent | React.KeyboardEvent): void {
    this.setState({ drawerOpen: false });
  }

  onChangeViewMode(treeMode: boolean): void {
    this.setState({ treeMode });
    Utils.setLocalStorageOption('treeMode', String(treeMode));
  }

  onSignUpComplete(_e: React.SyntheticEvent): void {
    window.location.reload();
  }

  onNavigation(
    e: React.MouseEvent,
    allowNavigationCallback: ((e: React.MouseEvent) => void) | null
  ): boolean {
    if (this.blockNavigation && this.navigateAwayCallback) {
      e.preventDefault();
      this.pendingNavigationEvent = e;
      this.allowNavigationCallback = allowNavigationCallback;
      this.navigateAwayCallback(e);
      return false;
    }
    if (allowNavigationCallback) allowNavigationCallback(e);
    return true;
  }

  onAllowNavigation(e: React.MouseEvent): void {
    if (this.pendingNavigationEvent) {
      this.blockNavigation = false;
      if (this.allowNavigationCallback) this.allowNavigationCallback(e);
      else (this.pendingNavigationEvent.target as HTMLElement).click();
    }
  }

  onBlockNavigation(
    blockNav: boolean,
    _blockLocation: string | null,
    callback: ((e: React.MouseEvent) => void) | null
  ): void {
    this.blockNavigation = blockNav;
    this.navigateAwayCallback = callback;
  }

  getGroupIdFromPath(location: Location): string {
    const pathSplit = location.pathname.split('/');
    if (pathSplit.length >= 3) return pathSplit[2];
    return 'team';
  }

  render() {
    const {
      location,
      history,
      navigate,
      params,
      match,
      store,
      onCloseChecklistPreviewDialog,
      onSaveChecklistAnswers,
    } = this.props;
    const {
      user,
      showSwitchUserDialog,
      error,
      unreadNotifications,
      themeName,
      theme,
      showDevDialog,
      showInactiveView,
      demoOrgResetting,
      showContactUsView,
      drawerOpen,
      showDeviceBreakpoints,
      contactUsRedirect,
      treeMode,
      dataVersion,
    } = this.state;

    // Groups come from AppDataProvider via props, wrapped to match old GroupsList structure
    const groupsArray = this.props.appData.groups;
    const groups = groupsArray
      ? {
          groups: groupsArray,
          get: (groupId: string) => groupsArray.find((g) => g.groupId === groupId),
        }
      : undefined;

    const { organization } = this.appContext;

    const showBillingBanner =
      user &&
      organization &&
      user.status !== CurrentUser.status.inactive &&
      user.hasAcceptedTerms &&
      organization.isTrial &&
      (organization.trialExpired || user.isOrganizationAdmin || user.isBillingUser) &&
      !organization.isDemo &&
      organization.status !== Organization.status.inactive;

    let pageMode = 'normal';
    if (showInactiveView) pageMode = 'inactive';
    else if (demoOrgResetting) pageMode = 'demoResetting';
    else if (error && error.isFail()) pageMode = 'error';
    // else if(window.app.env.MAINTENANCE_MODE)...

    return (
      <AppContext.Provider
        value={{
          ...this.appContext,
          theme,
        }}
      >
        <ThemeProvider theme={theme.muiTheme}>
          <div
            className={
              'klayo-layout klaro-theme_' +
              themeName +
              // (showBillingBanner ? ' klayo-layout--billingbanner' : '') +
              (Environment.isDev && App.allowDevBar ? ' klayo-layout--devmode' : '')
            }
          >
            <LoadingBar
              ref={this.setLoadingBarRef}
              sx={{ zIndex: 99999, width: '100vw', position: 'fixed' }}
            />

            {/* Try It Now preview sandbox: a persistent "sample data" banner, shown app-wide only
                while the preview is armed. */}
            {isPreviewArmed() && (
              <>
                {/* Fixed bar pinned to the top of the CONTENT column (right of the left nav drawer,
                    full-width on mobile). Paired with an in-flow spacer of the same height below, so
                    page content starts beneath the bar and is never covered while scrolling.
                    zIndex above content but below MUI dialogs (1300) so modals aren't blocked. */}
                <Box
                  sx={{
                    position: 'fixed',
                    top: 0,
                    left: { xs: 0, lg: '276px' },
                    right: 0,
                    zIndex: 1200,
                    height: PREVIEW_BANNER_HEIGHT,
                    boxSizing: 'border-box',
                    background: 'var(--neon-blue-nb-050)',
                    color: 'var(--grey-gr-900)',
                    px: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5,
                    fontSize: 14,
                  }}
                >
                  <span>
                    {isMyTeamActive()
                      ? 'You’re viewing your own team.'
                      : 'You’re exploring Klayo with sample data.'}
                  </span>
                  {/* "Show my team": flip between the synthetic sample and the TM's real students.
                      Only offered when the real logged-in user was a training manager. */}
                  {isMyTeamAvailable() && (
                    <Link
                      component='button'
                      type='button'
                      data-pf='show-my-team-toggle'
                      onClick={() =>
                        switchPreviewDataset(
                          isMyTeamActive() ? DEFAULT_DATASET_CODE : MY_TEAM_DATASET_CODE
                        )
                      }
                      sx={{
                        color: 'var(--neon-blue-nb-500)',
                        fontSize: 14,
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {isMyTeamActive() ? 'Show sample data' : 'Show my team'}
                    </Link>
                  )}
                  <Link
                    component='button'
                    type='button'
                    onClick={() => this.onExitDemoClick()}
                    sx={{
                      color: 'var(--neon-blue-nb-500)',
                      fontSize: 14,
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    Exit demo
                  </Link>
                </Box>
                {/* In-flow spacer: reserves the bar's height so content is pushed down, not covered. */}
                <Box sx={{ height: PREVIEW_BANNER_HEIGHT }} />

                {this.state.showExitDemoConfirm && (
                  <ConfirmationDialog
                    theme={theme}
                    title='Exit demo'
                    question={
                      <Stack spacing={2}>
                        <span>
                          You can always switch back to demo mode from the user menu. Just click on
                          your name card (bottom left corner) and select “Switch to demo data” from
                          the menu.
                        </span>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={this.state.dontShowExitDemoAgain}
                              onChange={(e) =>
                                this.setState({ dontShowExitDemoAgain: e.target.checked })
                              }
                            />
                          }
                          label="Don't show this dialog again"
                        />
                      </Stack>
                    }
                    cancelButton='Cancel'
                    acceptButton='Exit demo'
                    onCancel={() => this.setState({ showExitDemoConfirm: false })}
                    onAccept={() => this.onExitDemoConfirm()}
                  />
                )}
              </>
            )}

            {Environment.isDev && App.allowDevBar && <div className='klayo-devbar'></div>}

            {/* {showBillingBanner && (
              <BillingBanner
                onUpgradePlan={this.onUpgradePlan.bind(this)}
                organization={organization}
                user={user}
              />
            )} */}

            <AppDrawer
              ref={this.setAppDrawerRef}
              organization={organization}
              theme={theme}
              user={user}
              groups={groups}
              location={location}
              open={drawerOpen}
              devMode={this.appContext.devMode || Environment.isDev || Environment.isLocal}
              defaultNavItem={App.defaultPage}
              navItems={Object.values(AppPages).filter(
                (page: AppPage) =>
                  page.showInNav !== false &&
                  user &&
                  organization &&
                  page.hasAccess(user, organization) &&
                  // Optional nav-only gate: route may be reachable while the nav entry stays hidden.
                  (page.isVisibleInNav ? page.isVisibleInNav(user, organization) : true) &&
                  // Preview sandbox: restrict the menu to the demoed area(s) so the prospect's
                  // broad synthetic permissions don't expose unmocked pages.
                  (!isPreviewArmed() || PREVIEW_NAV_SLUGS.has(page.slug)) &&
                  // My groups lists no LearnUpon-linked groups, so a manager of only those gets no entry.
                  !(
                    page === AppPages.groups &&
                    groups &&
                    getMyGroupsNavGroups(groups.groups).length === 0
                  )
              )}
              getChildNavItems={(i) => {
                if (i === AppPages.groups && groups)
                  return getMyGroupsNavGroups(Object.values(groups.groups)).map((g: GroupItem) => ({
                    label: g.name,
                    path: '/groups/' + g.groupId,
                  }));
                else if (i === AppPages.settings)
                  return Object.values(Settings.subPages)
                    .filter(
                      (subPage: SettingsSubPage) =>
                        subPage.isShowInSideBar !== false &&
                        user &&
                        organization &&
                        subPage.hasAccess(user, organization)
                    )
                    .sort((a: SettingsSubPage, b: SettingsSubPage) => a.order - b.order)
                    .map((subPage: SettingsSubPage) => ({
                      label: subPage.name,
                      labelKey: subPage.nameKey,
                      path: subPage.paths[0],
                    }));
              }}
              // canChangeUser OR canChangeOrg, not canChangeOrg alone. The two flags are two switcher
              // modes — Lite (own organization) and Full (any organization) — and gating the menu entry
              // on the Full one meant a Lite user never saw "Switch user" at all, even though every
              // server-side path already supports them and SwitchUserDialog already hides the
              // organization picker for them. It looked fine locally and on dev only because devMode is
              // on there and DevDialog offers its own switch button off canChangeUser.
              allowSwitchUser={
                !!(user && (user.isDeveloper || user.canChangeUser || user.canChangeOrg))
              }
              unreadNotifications={unreadNotifications}
              onClose={this.onDrawerClose.bind(this)}
              onShowNotifications={this.onShowNotifications.bind(this)}
              onShowSwitchUserDialog={this.onShowSwitchUserDialog.bind(this)}
              onOpenDevDialog={this.onOpenDevDialog.bind(this)}
              onOpenSettings={this.onOpenSettings.bind(this)}
              onMyAccount={this.onMyAccount.bind(this)}
              onSignOut={this.onSignOut.bind(this)}
              // allowDemoSwitch={
              //   organization &&
              //   (organization.isDemo ||
              //     (user &&
              //       user.isOrganizationAdmin &&
              //       user.hasDemoEmployee &&
              //       organization.demoOrgStatus !== Organization.status.resetting &&
              //       theme.isBreakpointUp("md")))
              // }
              onDemoSwitch={this.onDemoSwitch.bind(this)}
              allowDemoDataSwitch={!!organization?.isTrial}
              onDemoDataSwitch={() =>
                isPreviewArmed()
                  ? exitPreview()
                  : enterPreview(
                      user ?? undefined,
                      organization ?? undefined,
                      'risk-assessment-mitigation',
                      'app_menu'
                    )
              }
              onNavigation={this.onNavigation.bind(this)}
              onLogoClick={this.onLogoClick.bind(this)}
            />

            <AppBar
              ref={this.setAppBarRef}
              organization={organization}
              location={location}
              theme={theme}
              user={user}
              onShowNotifications={this.onShowNotifications.bind(this)}
              onShowSwitchUserDialog={this.onShowSwitchUserDialog.bind(this)}
              onOpenDashboard={this.onOpenDashboard.bind(this)}
              onOpenDevDialog={this.onOpenDevDialog.bind(this)}
              onOpenSettings={this.onOpenSettings.bind(this)}
              onSwitchTheme={this.setTheme.bind(this)}
              unreadNotifications={unreadNotifications}
              onSignIn={this.onSignIn.bind(this)}
              onSignOut={this.onSignOut.bind(this)}
              themeName={themeName}
              defaultNavItem={App.defaultPage}
              navItems={Object.values(AppPages).filter(
                (page: AppPage) =>
                  page.showInNav !== false &&
                  user &&
                  organization &&
                  page.hasAccess(user, organization) &&
                  // Optional nav-only gate: route may be reachable while the nav entry stays hidden.
                  (page.isVisibleInNav ? page.isVisibleInNav(user, organization) : true) &&
                  // Preview sandbox: restrict the menu to the demoed area(s) so the prospect's
                  // broad synthetic permissions don't expose unmocked pages.
                  (!isPreviewArmed() || PREVIEW_NAV_SLUGS.has(page.slug)) &&
                  // My groups lists no LearnUpon-linked groups, so a manager of only those gets no entry.
                  !(
                    page === AppPages.groups &&
                    groups &&
                    getMyGroupsNavGroups(groups.groups).length === 0
                  )
              )}
              showActions={true}
              onLogoClick={this.onLogoClick.bind(this)}
              onDemoSwitch={this.onDemoSwitch.bind(this)}
              onMenuButtonClick={this.onMenuButtonClick.bind(this)}
              onNavigation={this.onNavigation.bind(this)}
              sx={{ display: { xs: 'flex', lg: 'none' } }}
            />

            <ErrorBoundary>
              {pageMode === 'error' && (
                <Fade in={true}>
                  <Box sx={{ marginLeft: { xs: '0', lg: '276px' } }}>
                    <ErrorPage theme={theme} error={error} />
                  </Box>
                </Fade>
              )}

              {pageMode === 'inactive' && (organization || user) && (
                <Fade in={true}>
                  <Box sx={{ marginLeft: { xs: '0', lg: '276px' } }}>
                    <InactiveView
                      organization={organization}
                      user={user}
                      theme={theme}
                      demoOrgResetting={demoOrgResetting}
                      onBuyClick={this.onUpgradePlan.bind(this)}
                      onContactClick={this.onShowContactForm.bind(this)}
                    />
                  </Box>
                </Fade>
              )}

              {pageMode === 'demoResetting' && organization && user && (
                <Fade in={true}>
                  <div>
                    <UnavailableView
                      organization={organization}
                      user={user}
                      theme={theme}
                      heading='Your demo organization is resetting'
                      message={
                        <div>
                          Please wait while your Demo organization data is being reset. This may
                          take a few minutes. Please try again soon.
                          <br />
                          <br />
                          Alternatively, you can switch to your organization.
                        </div>
                      }
                      actions={['switchOrg', 'tryAgain']}
                      demoOrgResetting={demoOrgResetting}
                      onSwitchToOrganization={this.onDemoSwitch.bind(this)}
                    />
                  </div>
                </Fade>
              )}

              {pageMode === 'normal' && (
                <Suspense
                  fallback={
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        height: '100vh',
                        justifyContent: 'center',
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  }
                >
                  <Routes>
                    {Object.values(AppPages)
                      .filter((page: AppPage) =>
                        user && organization
                          ? page.hasAccess(user, organization)
                          : !(
                              this.props.appData.userLoading ||
                              this.props.appData.organizationLoading
                            )
                      )
                      .sort(
                        (a: AppPage, b: AppPage) =>
                          Number(a === App.defaultPage) - Number(b === App.defaultPage)
                      )
                      .map((page: AppPage, index: number) => {
                        const componentProps = {
                          ref: page.ref,
                          location,
                          history,
                          navigate,
                          params,
                          match,
                          organization,
                          user,
                          groups,
                          theme,
                          treeMode,
                          dataVersion,
                          // showBillingBanner,
                          onShowError: this.onShowError.bind(this),
                          onChangeViewMode: this.onChangeViewMode.bind(this),
                          onNavigation: this.onNavigation.bind(this),
                          onBlockNavigation: this.onBlockNavigation.bind(this),
                          onAllowNavigation: this.onAllowNavigation.bind(this),
                          ...this.getPageProps(page),
                        };

                        const pageElement = (
                          <Fade in={true}>
                            <Box
                              sx={
                                ['onboarding', 'signup'].includes(page.slug)
                                  ? {
                                      // Sits above app chrome (appBar 1100, drawer 1200) but below
                                      // MUI's modal/popover layer (1300) so Select/Autocomplete
                                      // dropdowns and dialogs opened within onboarding render on top
                                      // instead of behind this overlay.
                                      zIndex: 1250,
                                      position: 'absolute',
                                      width: '100%',
                                      height: '100%',
                                    }
                                  : { marginLeft: { xs: '0', lg: '276px' } }
                              }
                            >
                              {['team', 'groups'].includes(page.slug) ? (
                                <TeamProvider
                                  {...(page.slug === 'groups' && {
                                    key: this.getGroupIdFromPath(location),
                                  })}
                                >
                                  {React.createElement(PageComponents[page.slug], {
                                    ...componentProps,
                                    ...(page.slug === 'groups' && {
                                      key: this.getGroupIdFromPath(location),
                                    }),
                                  })}
                                </TeamProvider>
                              ) : (
                                React.createElement(
                                  PageComponents[page.slug],
                                  componentProps as React.Attributes
                                )
                              )}
                            </Box>
                          </Fade>
                        );

                        // For the default page, also match "/"
                        if (page === App.defaultPage) {
                          // Org-level override: land full-access employees in My Klayo instead of
                          // My Profile. Applies only to full-access employees, and only when the
                          // MyKlayo feature is enabled; otherwise keep the default (Profile).
                          // Target the My Klayo index so its own default redirect decides the tab:
                          // My Courses when the user has active enrolments, else the Course Catalogue.
                          const landInMyKlayo =
                            user &&
                            organization &&
                            user.isFullAccess &&
                            !user.isMyKlayoUser &&
                            organization.employeeLandingPage ===
                              Organization.employeeLandingPage.MyKlayo &&
                            organization.enabledFeatures?.MyKlayo !== false;

                          return [
                            <Route
                              key={index}
                              path={'/' + page.slug + '/*'}
                              element={pageElement}
                            />,
                            <Route
                              key={index + '-default'}
                              path='/'
                              element={
                                landInMyKlayo ? <Navigate to='/my-klayo' replace /> : pageElement
                              }
                            />,
                          ];
                        }

                        // For groups, include :groupId in the path so Team's Routes see paths without the groupId
                        if (page.slug === 'groups') {
                          return (
                            <Route key={index} path='/groups/:groupId/*' element={pageElement} />
                          );
                        }

                        return (
                          <Route key={index} path={'/' + page.slug + '/*'} element={pageElement} />
                        );
                      })}
                    {user && organization && organization.enabledFeatures?.MyKlayo !== false && (
                      <Route
                        path='/'
                        element={
                          <Navigate
                            to={
                              user.isTrainingManager && !user.isFullAccess
                                ? '/settings/learning'
                                : '/my-klayo'
                            }
                            replace
                          />
                        }
                      />
                    )}
                    {user && organization && (
                      <Route path='*' element={<Navigate to='/' replace />} />
                    )}
                  </Routes>
                </Suspense>
              )}
            </ErrorBoundary>
          </div>
          {showSwitchUserDialog && (
            <SwitchUserDialog
              organization={organization}
              user={user}
              theme={theme}
              onClose={this.closeSwitchUserDialog.bind(this)}
              onSwitchUser={this.onSwitchUser.bind(this)}
            />
          )}

          {user && (
            <NotificationDrawer
              ref={this.setNotificationDrawerRef}
              organization={organization}
              user={user}
              groups={groups ? groups.groups.filter((g: GroupItem) => g.hasManagePermission) : null}
              onLoadNotifications={this.onLoadNotifications.bind(this)}
              onNotificationClick={this.onNotificationClick.bind(this)}
              onNavigation={this.onNavigation.bind(this)}
            />
          )}

          <NotificationPopup ref={this.setNotificationPopupRef} />

          {/* KIM is hidden in the Try It Now preview: its /Kit/chat POST would be rejected by the
              fail-closed mock (ADR 0001), so a prospect opening it gets a broken demo chat. Not
              mounting it is cleaner than relying on the rejection. */}
          {user && !isPreviewArmed() && (
            <>
              <KitFloatingButton onClick={() => this.kitDrawer?.open()} />
              <KitDrawer
                ref={this.setKitDrawerRef}
                onNavigate={(path: string) => this.props.history.push(path)}
              />
            </>
          )}

          {showDevDialog && (
            <DevDialog
              user={user}
              theme={theme}
              organization={organization}
              onShowSwitchUserDialog={this.onShowSwitchUserDialog.bind(this)}
              onSwitchUser={this.onSwitchUser.bind(this)}
              onClose={this.onCloseDevDialog.bind(this)}
            />
          )}

          {error && !error.isFail() && (
            <Snackbar
              open={true}
              type='error'
              duration={6000}
              onClose={this.onCloseErrorSnackbar.bind(this)}
              message={error.message}
            />
          )}

          {showContactUsView && (
            <ContactUsDialog
              organization={organization}
              user={user}
              onClose={() => this.onCloseContactUsDialog()}
            />
          )}

          {showDeviceBreakpoints && <BreakpointIndicator theme={theme} />}
          <CheckListsPreviewDialog
            isOpen={store.checklist.isOpenChecklistPreviewDialog ?? false}
            onClose={() => onCloseChecklistPreviewDialog()}
            checklistData={store.checklist.checklistData}
            checklistId={store.checklist.checklistId}
            onSave={(answers) => onSaveChecklistAnswers(answers)}
            isEditMode={store.checklist.isEditMode}
            disabled={store.checklist.disabled}
          />
        </ThemeProvider>
      </AppContext.Provider>
    );
  }
}

export default withAppData(withRedux(withNavigate(App, { legacyMode: true })));
